import { useCallback, useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react'

// ── Tunable constants ────────────────────────────────────────────────────────
// Snapshots are taken when the user pauses typing (>= SNAPSHOT_PAUSE_MS) and
// no more often than SNAPSHOT_MIN_INTERVAL_MS. Continuous typing snapshots
// every ~5 seconds; pauses snapshot once the user stops. MAX_HISTORY caps
// the buffer; older entries drop off the front.
const MAX_HISTORY = 10
const SNAPSHOT_MIN_INTERVAL_MS = 5000
const SNAPSHOT_PAUSE_MS = 800

// Imperative handle exposed via `handleRef` - parent components hook these
// up to external Undo / Redo toolbar buttons.
export interface UndoRedoHandle {
  undo: () => void
  redo: () => void
}

// Pushed to `onStateChange` whenever the reachable bounds flip. Parents store
// these in state to enable / disable their toolbar buttons.
export interface UndoRedoState {
  canUndo: boolean
  canRedo: boolean
}

interface UseUndoRedoHistoryOptions<T> {
  // Current upstream value. Used to seed the buffer on first mount.
  value: T | undefined
  // Where mutations are applied. The hook returns a `wrappedOnChange` that
  // wraps this; callers pass the wrapped version to their inner editor.
  onChange: (next: T) => void
  // Optional imperative handle for external Undo / Redo buttons.
  handleRef?: Ref<UndoRedoHandle>
  // Fires whenever canUndo / canRedo change.
  onStateChange?: (state: UndoRedoState) => void
  // Equality test for "is this value the same as the last snapshot?".
  // Defaults to JSON.stringify equality (cheap enough for our payload sizes).
  // Pass `(a, b) => a === b` for primitive strings / numbers.
  equals?: (a: T, b: T) => boolean
  // When false the hook is a thunk - wrappedOnChange just forwards, undo /
  // redo are no-ops, no snapshots are taken. Useful if the host wants to
  // disable history without conditionally calling the hook.
  enabled?: boolean
}

interface UseUndoRedoHistoryResult<T> {
  // Drop-in replacement for the host's onChange - call this from the inner
  // editor instead of the raw onChange. Commits upstream AND schedules a
  // history snapshot (unless the change came from undo/redo).
  wrappedOnChange: (next: T) => void
  undo: () => void
  redo: () => void
  canUndo: boolean
  canRedo: boolean
}

const defaultEquals = <T,>(a: T, b: T) => JSON.stringify(a) === JSON.stringify(b)

// Shared undo / redo history. Used by both TaResponse (TextStructure) and
// TaQueryBar (HTML string). The hook owns its buffer in refs, exposes
// imperative undo/redo, and emits state changes for toolbar buttons. Edits
// that come back through `wrappedOnChange` schedule a pause-aware snapshot;
// programmatic undo / redo bypass that snapshot so they don't pollute the
// buffer with their own application.
export function useUndoRedoHistory<T>({
  value, onChange, handleRef, onStateChange,
  equals = defaultEquals, enabled = true,
}: UseUndoRedoHistoryOptions<T>): UseUndoRedoHistoryResult<T> {

  // Buffer of past values; `index` cursor points at the current state. Refs
  // so the snapshot timer can read/write without re-binding every keystroke.
  const historyRef = useRef<T[]>([])
  const indexRef = useRef<number>(-1)
  const lastSnapshotAtRef = useRef<number>(0)
  const pendingValueRef = useRef<T | undefined>(value)
  const snapshotTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Set to true when we apply a value programmatically (undo / redo); the
  // resulting onChange echo should not snapshot itself.
  const skipNextSnapshotRef = useRef(false)

  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)

  const recomputeFlags = useCallback(() => {
    const nextUndo = indexRef.current > 0
    const nextRedo = indexRef.current >= 0 && indexRef.current < historyRef.current.length - 1
    setCanUndo(prev => prev === nextUndo ? prev : nextUndo)
    setCanRedo(prev => prev === nextRedo ? prev : nextRedo)
  }, [])

  // Seed the buffer with the initial value, and snapshot any external
  // value changes (parent set a new value without going through
  // `wrappedOnChange` - Word Wizard Apply, taskstream chunk
  // replacement, "clear answer", etc.) so the user can undo back to
  // where they were before the batch operation. Internal commits set
  // `pendingValueRef.current` so the matching prop echo here is a
  // no-op.
  useEffect(() => {
    if (!enabled) return
    // Initial seed.
    if (historyRef.current.length === 0) {
      if (value !== undefined) {
        historyRef.current = [structuredClone(value)]
        indexRef.current = 0
        lastSnapshotAtRef.current = Date.now()
        pendingValueRef.current = value
        recomputeFlags()
      }
      return
    }
    // External change - value diverged from the last-committed
    // pending value. Flush any pending pause-snapshot first so undo
    // can return to the user's most recent in-progress typing before
    // jumping back further.
    if (value !== undefined && value !== pendingValueRef.current) {
      takeSnapshot()
      if (snapshotTimerRef.current) {
        clearTimeout(snapshotTimerRef.current)
        snapshotTimerRef.current = null
      }
      const head = historyRef.current[indexRef.current]
      if (head === undefined || !equals(head, value)) {
        const trimmed = historyRef.current.slice(0, indexRef.current + 1)
        trimmed.push(structuredClone(value))
        while (trimmed.length > MAX_HISTORY) trimmed.shift()
        historyRef.current = trimmed
        indexRef.current = trimmed.length - 1
        lastSnapshotAtRef.current = Date.now()
        recomputeFlags()
      }
      pendingValueRef.current = value
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, enabled])

  useEffect(() => {
    onStateChange?.({ canUndo, canRedo })
  }, [canUndo, canRedo, onStateChange])

  const takeSnapshot = useCallback(() => {
    if (!enabled) return
    const v = pendingValueRef.current
    if (v === undefined) return
    const head = historyRef.current[indexRef.current]
    if (head !== undefined && equals(head, v)) return
    // Drop any forward history past the cursor (we just diverged from it).
    const trimmed = historyRef.current.slice(0, indexRef.current + 1)
    trimmed.push(structuredClone(v))
    while (trimmed.length > MAX_HISTORY) trimmed.shift()
    historyRef.current = trimmed
    indexRef.current = trimmed.length - 1
    lastSnapshotAtRef.current = Date.now()
    recomputeFlags()
  }, [enabled, equals, recomputeFlags])

  // Re-arm the snapshot timer. Effective delay =
  //   max(pause-since-last-keystroke, interval-remaining-since-last-snapshot)
  // so a snapshot fires only when the user has paused AND at least
  // SNAPSHOT_MIN_INTERVAL_MS has elapsed since the previous one.
  const scheduleSnapshot = useCallback(() => {
    if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current)
    const now = Date.now()
    const timeSinceLastSnap = now - lastSnapshotAtRef.current
    const remainingInterval = Math.max(0, SNAPSHOT_MIN_INTERVAL_MS - timeSinceLastSnap)
    const delay = Math.max(SNAPSHOT_PAUSE_MS, remainingInterval)
    snapshotTimerRef.current = setTimeout(() => { takeSnapshot() }, delay)
  }, [takeSnapshot])

  const wrappedOnChange = useCallback((next: T) => {
    onChange(next)
    pendingValueRef.current = next
    if (skipNextSnapshotRef.current) {
      skipNextSnapshotRef.current = false
      return
    }
    if (enabled) scheduleSnapshot()
  }, [onChange, scheduleSnapshot, enabled])

  const undo = useCallback(() => {
    if (!enabled) return
    // Flush any pending snapshot so the current text isn't lost when we jump
    // back - otherwise an in-flight pause-snapshot would land after we've
    // already moved the index.
    if (snapshotTimerRef.current) { clearTimeout(snapshotTimerRef.current); snapshotTimerRef.current = null }
    takeSnapshot()
    if (indexRef.current <= 0) return
    indexRef.current -= 1
    const prev = historyRef.current[indexRef.current]
    if (prev === undefined) return
    skipNextSnapshotRef.current = true
    onChange(structuredClone(prev))
    pendingValueRef.current = prev
    recomputeFlags()
  }, [enabled, onChange, takeSnapshot, recomputeFlags])

  const redo = useCallback(() => {
    if (!enabled) return
    if (indexRef.current >= historyRef.current.length - 1) return
    indexRef.current += 1
    const next = historyRef.current[indexRef.current]
    if (next === undefined) return
    skipNextSnapshotRef.current = true
    onChange(structuredClone(next))
    pendingValueRef.current = next
    recomputeFlags()
  }, [enabled, onChange, recomputeFlags])

  useImperativeHandle(handleRef, () => ({ undo, redo }), [undo, redo])

  // Clean up the pending snapshot timer on unmount.
  useEffect(() => () => {
    if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current)
  }, [])

  return { wrappedOnChange, undo, redo, canUndo, canRedo }
}
